import { jsx as _jsx } from "react/jsx-runtime";
import { getTimeValue, createTemperatureString, formatNumberString, checkCommonPlural, } from '.';
import { ComparisonType, IntervalUnit, SentinelType, } from '../types';
import { KW_UNIT } from './constants';
import globalVariables from './global';
import { AmbientSensor as AmbientSensorIcon, ControlsHealth as ControlsHealthIcon, DoorSensor as DoorIcon, DoorSirenOn, EnergySensor as IconEnergySensor, GasStation as GasStationIcon, GatewayLowBattery as GatewayLowBatteryIcon, GatewayOffline as GatewayOfflineIcon, HumiditySensor as HumidityIcon, LocalOverride as SiteControllerLocalOverrideIcon, ThermostatOverride as ThermostatLocalOverrideIcon, TemperatureSensor as TemperatureIcon, } from '../icons';
import { getTempDifferenceText } from './temperature';
export const getSentinelConditionText = (sentinel, currentUser, targetId) => {
    const { sentinelType, parameters } = sentinel;
    switch (sentinelType) {
        case SentinelType.TEMPERATURE:
        case SentinelType.HUMIDITY:
        case SentinelType.ACTIVE_POWER_THRESHOLD:
            return getSentinelComparisonText(sentinel, currentUser, targetId);
        case SentinelType.TEMPERATURE_AND_DOOR: {
            return `Door opened and Temperature is ${getSentinelComparisonText(sentinel, currentUser, targetId)}`;
        }
        case SentinelType.BINARY:
        case SentinelType.DOOR_SIREN_CHECK: {
            const { openClosed } = parameters;
            if (openClosed === undefined)
                return globalVariables.NOT_AVAILABLE;
            return openClosed === 'OPEN' ? 'Door Open' : 'Door Close';
        }
        case SentinelType.BATTERY_LEVEL_CHECK:
            return '';
        case SentinelType.CONNECTIVITY:
            return '';
        case SentinelType.BINARY_COUNTER: {
            const { maxOpeningCount } = parameters;
            return `Door opened more than ${maxOpeningCount} times in 24 hours`;
        }
        case SentinelType.CUSTOMER_COMFORT:
            return '';
        case SentinelType.GATEWAY_ONLINE_STATUS:
            return '';
        case SentinelType.ENERGYPRO_ONLINE_STATUS:
            return '';
        case SentinelType.CONTROLS_HEALTH_CHECK:
            return '';
        case SentinelType.SITE_CONTROLLER_LOCAL_OVERRIDE_CHECK:
            return '';
        case SentinelType.THERMOSTAT_LOCAL_OVERRIDE_CHECK:
            return 'Device in Override';
    }
};
const getSentinelComparisonText = (sentinel, currentUser, targetId) => {
    const { sentinelType, parameters: { comparisonType, threshold, rangeLowerBound, rangeUpperBound, targetsTolerance, }, } = sentinel;
    let conditionText = globalVariables.NOT_AVAILABLE;
    if (!comparisonType)
        return conditionText;
    const tolerance = (targetId && (targetsTolerance === null || targetsTolerance === void 0 ? void 0 : targetsTolerance[Number(targetId)])) || 0;
    const toleranceText = tolerance
        ? `\n(Tolerance: ${getTempDifferenceText(currentUser, tolerance, true)})`
        : '';
    switch (comparisonType) {
        case ComparisonType.eq: {
            if (threshold === undefined)
                return conditionText;
            const conditionValue = formatSentinelNumberValue(sentinelType, threshold, currentUser);
            conditionText = `Equal to ${conditionValue}`;
            break;
        }
        case ComparisonType.lt: {
            if (threshold === undefined)
                return conditionText;
            const conditionValue = formatSentinelNumberValue(sentinelType, threshold, currentUser, -tolerance);
            conditionText = `Less than ${conditionValue}` + toleranceText;
            break;
        }
        case ComparisonType.gt: {
            if (threshold === undefined)
                return conditionText;
            const conditionValue = formatSentinelNumberValue(sentinelType, threshold, currentUser, tolerance);
            conditionText = `Greater than ${conditionValue}` + toleranceText;
            break;
        }
        case ComparisonType.inner: {
            if (rangeLowerBound === undefined || rangeUpperBound === undefined)
                return conditionText;
            const conditionValueLower = formatSentinelNumberValue(sentinelType, rangeLowerBound, currentUser, tolerance);
            const conditionValueUpper = formatSentinelNumberValue(sentinelType, rangeUpperBound, currentUser, -tolerance);
            conditionText =
                `Between ${conditionValueLower} and ${conditionValueUpper}` +
                    toleranceText;
            break;
        }
        case ComparisonType.outer: {
            if (rangeLowerBound === undefined || rangeUpperBound === undefined)
                return conditionText;
            const conditionValueLower = formatSentinelNumberValue(sentinelType, rangeLowerBound, currentUser, -tolerance);
            const conditionValueUpper = formatSentinelNumberValue(sentinelType, rangeUpperBound, currentUser, tolerance);
            conditionText =
                `Less than ${conditionValueLower} and greater than ${conditionValueUpper}` +
                    toleranceText;
            break;
        }
    }
    return conditionText;
};
export const getSentinelDelayText = ({ delay: { value, unit } }) => {
    if (unit === IntervalUnit.DEVICEINTERVAL) {
        return `Notify after ${checkCommonPlural('device interval', value)} of delay time`;
    }
    const hours = getTimeValue(value, 'hour');
    const minutes = getTimeValue(value, 'minute');
    if (hours) {
        return `Notify after ${checkCommonPlural('hour', hours)} ${checkCommonPlural('minute', minutes)}`;
    }
    if (minutes) {
        return `Notify after ${checkCommonPlural('minute', minutes)}`;
    }
    return '';
};
export const formatSentinelNumberValue = (sentinelType, value, currentUser, addition) => {
    switch (sentinelType) {
        case SentinelType.TEMPERATURE:
        case SentinelType.TEMPERATURE_AND_DOOR:
            return createTemperatureString(value + (addition || 0), currentUser);
        case SentinelType.HUMIDITY:
            return formatNumberString(value, {
                numDecimals: 2,
                unit: '%',
            });
        case SentinelType.ACTIVE_POWER_THRESHOLD:
            return formatNumberString(value, {
                numDecimals: 2,
                unit: KW_UNIT,
            });
    }
};
export function SentinelTypeIcon({ type, size, }) {
    switch (type) {
        case SentinelType.TEMPERATURE:
            return _jsx(TemperatureIcon, { size: size }, void 0);
        case SentinelType.BINARY_COUNTER:
        case SentinelType.BINARY:
            return _jsx(DoorIcon, { size: size }, void 0);
        case SentinelType.DOOR_SIREN_CHECK:
            return _jsx(DoorSirenOn, { size: size }, void 0);
        case SentinelType.HUMIDITY:
            return _jsx(HumidityIcon, { size: size }, void 0);
        case SentinelType.BATTERY_LEVEL_CHECK:
            return _jsx(GatewayLowBatteryIcon, { size: size }, void 0);
        case SentinelType.CONNECTIVITY:
            return _jsx(GatewayOfflineIcon, { size: size }, void 0);
        case SentinelType.CUSTOMER_COMFORT:
            return _jsx(AmbientSensorIcon, { size: size }, void 0);
        case SentinelType.TEMPERATURE_AND_DOOR:
            return _jsx(GasStationIcon, { size: size }, void 0);
        case SentinelType.ACTIVE_POWER_THRESHOLD:
            return _jsx(IconEnergySensor, { size: size }, void 0);
        case SentinelType.CONTROLS_HEALTH_CHECK:
            return _jsx(ControlsHealthIcon, { size: size }, void 0);
        case SentinelType.SITE_CONTROLLER_LOCAL_OVERRIDE_CHECK:
            return _jsx(SiteControllerLocalOverrideIcon, { size: size }, void 0);
        case SentinelType.THERMOSTAT_LOCAL_OVERRIDE_CHECK:
            return _jsx(ThermostatLocalOverrideIcon, { size: size }, void 0);
        case SentinelType.NONE:
        default:
            return null;
    }
}
